import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUserLocation } from '../components/hooks/useUserLocation';
import { calculateDistance, calculateBearing, getDirectionFromBearing } from '../components/utils/locationUtils';
import placesData from '../components/data/SK.json';
import '../components/css/ResultsPage.css';
import Sort from '../components/Sort';
import OpenStatus, { computeOpenStatus } from '../components/OpenStatus';
import StarRating from '../components/StarRating';
import Header2 from '../components/Header2';
import Maps from '../components/Maps';
import { useUser } from '../components/UserContext';
import { getFirestore, getDoc, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import Fuse from 'fuse.js';
import synonyms from '../components/data/synonyms.json'; // Import synonyms data

// --- Normalize Slovak characters (remove diacritics & symbols) ---
const normalizeSlovak = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD') // separates accents
    .replace(/[\u0300-\u036f]/g, '') // removes accents
    .replace(/[^a-z0-9\s]/g, ''); // removes punctuation/symbols
};

function ResultsPage() {
  const [hoveredStar, setHoveredStar] = useState(0);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOption, setSortOption] = useState('recommended');
  const [favorites, setFavorites] = useState([]);
  const location = useLocation();
  const { userLocation, error } = useUserLocation();
  const { user } = useUser();
  const db = getFirestore();
  const [filters, setFilters] = useState({
    rating: 'All',
    distanceMin: 0,
    distanceMax: 5000,
    openNow: 'All',
    verified: 'All',
  });
  const [popupMessage, setPopupMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);

  const resetFilters = () => {
    setFilters({
      rating: 'All',
      distanceMin: 0,
      distanceMax: 5000,
      openNow: 'All',
      verified: 'All',
    });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => {
      let newValue = value;
      if (filterName === 'distanceMin' && value > prevFilters.distanceMax) {
        newValue = prevFilters.distanceMax;
      }
      if (filterName === 'distanceMax' && value < prevFilters.distanceMin) {
        newValue = prevFilters.distanceMin;
      }

      return {
        ...prevFilters,
        [filterName]: newValue,
      };
    });
  };

  const getSearchQuery = () => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  };

  useEffect(() => {
    if (!user) return;

    const fetchFavorites = async () => {
      try {
        const userRef = doc(db, 'Users', user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          setFavorites(userData.favorites || []);
        }
      } catch (error) {
        console.error('Error fetching favorites:', error);
        alert('An error occurred while fetching your favorites.');
      }
    };

    fetchFavorites();
  }, [user, db]);

  useEffect(() => {
    const query = normalizeSlovak(getSearchQuery());

    // Map query to synonym type (ignores Slovak diacritics)
    let mappedCategory = query;
    if (query) {
      for (const [type, words] of Object.entries(synonyms)) {
        if (words.some(word => normalizeSlovak(word) === query)) {
          mappedCategory = type;
          break;
        }
      }
    }

    // Normalize data for Fuse.js
    const normalizedData = placesData.map(place => ({
      ...place,
      name: normalizeSlovak(place.name),
      types: place.types?.map(t => normalizeSlovak(t)) || [],
      search_keyword: normalizeSlovak(place.search_keyword),
    }));

    const fuse = new Fuse(normalizedData, {
      keys: ['name', 'types', 'search_keyword'],
      threshold: 0.2, // precise but now diacritic-insensitive
      includeScore: true,
    });

    const fuseResults = query
      ? fuse.search(normalizeSlovak(mappedCategory))
      : normalizedData.map((item) => ({ item }));

    const filteredPlaces = fuseResults
      .map((result) => result.item)
      .filter((place) => {
        if (filters.rating !== 'All' && place.rating < filters.rating) return false;
        if (filters.openNow === 'Yes' && computeOpenStatus(place.opening_hours).sortStatus !== 'open') return false;
        if (filters.verified === 'Yes' && place.premium !== '1') return false;
        if (place.distance < filters.distanceMin || place.distance > filters.distanceMax) return false;
        return true;
      })
      .map((place) => {
        if (userLocation) {
          const distance = calculateDistance(
            userLocation.latitude,
            userLocation.longitude,
            place.latitude,
            place.longitude
          );

          const bearing = calculateBearing(
            userLocation.latitude,
            userLocation.longitude,
            place.latitude,
            place.longitude
          );

          const direction = getDirectionFromBearing(bearing);

          return { ...place, distance: Math.round(distance), direction };
        }
        return place;
      })
      .sort((a, b) => {
        const MAX_PREMIUM_DISTANCE = 20000; // 20 km limit for premium boost

        switch (sortOption) {
          case 'closest':
            return a.distance - b.distance;

          case 'highestRated':
            if (a.rating !== b.rating) return b.rating - a.rating;
            return b.user_ratings_total - a.user_ratings_total;

          case 'recommended':
          default:
            const statusA = computeOpenStatus(a.opening_hours).sortStatus;
            const statusB = computeOpenStatus(b.opening_hours).sortStatus;
            const priority = { open: 1, closed: 2, 'no-hours': 3 };

            // Determine if premium and within range
            const aIsPremiumNearby = a.premium === '1' && a.distance <= MAX_PREMIUM_DISTANCE;
            const bIsPremiumNearby = b.premium === '1' && b.distance <= MAX_PREMIUM_DISTANCE;

            // ✅ Premium (nearby only) first
            if (aIsPremiumNearby && !bIsPremiumNearby) return -1;
            if (!aIsPremiumNearby && bIsPremiumNearby) return 1;

            // ✅ Then open status (open → closed → no-hours)
            if (priority[statusA] !== priority[statusB])
              return priority[statusA] - priority[statusB];

            // ✅ Then distance (closer wins)
            return a.distance - b.distance;
        }
      })
      .slice(0, 20);


    const distances = filteredPlaces.map((place) => place.distance).filter(Boolean);
    const minDistance = distances.length ? Math.min(...distances) : 0;
    const maxDistance = distances.length ? Math.max(...distances) : 5000;

    setPlaces(filteredPlaces);
    // Only update filters if min/max distances actually changed to avoid update loops
    setFilters((prevFilters) => {
      const newMin = minDistance;
      const newMax = maxDistance;
      if (prevFilters.distanceMin === newMin && prevFilters.distanceMax === newMax) {
        return prevFilters; // no change
      }
      return {
        ...prevFilters,
        distanceMin: newMin,
        distanceMax: newMax,
      };
    });

    setLoading(false);
  }, [location.search, userLocation, sortOption, filters]);

  const handleFavorite = async (placeId) => {
    if (!user) {
      alert('You must be logged in to favorite a place.');
      return;
    }

    try {
      const userRef = doc(db, 'Users', user.uid);
      if (favorites.includes(placeId)) {
        await updateDoc(userRef, { favorites: arrayRemove(placeId) });
        setFavorites(favorites.filter((id) => id !== placeId));
        setPopupMessage('Vymazané z obľúbených');
      } else {
        await updateDoc(userRef, { favorites: arrayUnion(placeId) });
        setFavorites([...favorites, placeId]);
        setPopupMessage('Pridané do obľúbených!');
      }

      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2000);
    } catch (error) {
      console.error('Error updating favorites:', error);
      alert('An error occurred while updating favorites.');
    }
  };

  return (
    <>
      <Header2 />
      <Maps places={places} />
      <div className="shadow"></div>
      <div className="results-page">
        {showPopup && <div className="popup-message">{popupMessage}</div>}

        {/* Filters Section */}
        <aside className="filters">
          <h3>Filtre</h3>
          <div className="filter-tile">
            <label>Hodnotenie</label>
            <div className="stars" onMouseLeave={() => setHoveredStar(0)}>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={star <= (hoveredStar || filters.rating) ? 'selected' : ''}
                  onMouseEnter={() => setHoveredStar(star)}
                  onClick={() => handleFilterChange('rating', star)}
                >
                  <i className="fa-regular fa-star"></i>
                </span>
              ))}
            </div>
          </div>

          <div className="filter-tile">
            <label>Otvorené teraz</label>
            <div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={filters.openNow === 'Yes'}
                  onChange={(e) => handleFilterChange('openNow', e.target.checked ? 'Yes' : 'No')}
                />
                <span className="toggle-switch"></span>
              </label>
            </div>
          </div>

          <div className="filter-tile">
            <label>Overený užívateľ</label>
            <div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={filters.verified === 'Yes'}
                  onChange={(e) => handleFilterChange('verified', e.target.checked ? 'Yes' : 'No')}
                />
                <span className="toggle-switch"></span>
              </label>
            </div>
          </div>

          <button className="reset-filters" onClick={resetFilters}>
            Resetovať filtre <i className="fa-solid fa-eraser"></i>
          </button>
        </aside>

        <div className="content">
          <Sort setSortOption={setSortOption} />
          <p className="no-of-results">Výsledkov: {places.length}</p>
          {error && <p className="error">Pre fungovanie stránky je nutné povoliť zdieľanie polohy</p>}
          <br />
          {places.length === 0 && !loading ? (
            <p>Žiadne výsledky pre "{getSearchQuery()}"</p>
          ) : (
            <div className="products-grid">
              {places.map((place) => (
                <div
                  className={`product-tile ${place.premium === '1' ? 'premium-border' : ''}`}
                  key={place.place_id}
                  style={{ position: 'relative' }}
                >
                  {place.premium === '1' && (
                    <div className="recommended-box">
                      <i className="fa-solid fa-check"></i> Overený užívateľ
                    </div>
                  )}
                  <div
                    className="tile-image"
                    style={{
                      backgroundImage: `url(${place.short_photo_references?.length > 0
                        ? `/images/${place.short_photo_references[0]}.jpg`
                        : `/logo_orig.png`
                        })`,
                    }}
                  ></div>
                  <div className="tile-content">
                    <h2>
                      <Link to={`/catalog/${place.place_id}`}>
                        {place.name.length > 40 ? `${place.name.substring(0, 40)}...` : place.name}
                      </Link>
                    </h2>
                    <p>
                      <a href={place.google_maps_url} target="_blank" rel="noopener noreferrer">
                        {place.address.length > 40 ? `${place.address.substring(0, 40)}...` : place.address}
                      </a>
                    </p>
                    <br />
                    <div className="place-rating">
                      <StarRating rating={place.rating} />
                      <p>
                        {place?.user_ratings_total
                          ? `Hodnotilo: ${place.user_ratings_total}`
                          : 'Žiadne hodnotenia'}
                      </p>
                    </div>
                    {place.distance !== undefined && (
                      <p>
                        <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
                        {place.distance} metrov<span> {place.direction}</span>
                      </p>
                    )}
                    <OpenStatus openingHours={place.opening_hours} />
                  </div>
                  <div
                    className="favorite-button"
                    onClick={() => handleFavorite(place.place_id)}
                  >
                    <i
                      className={`fa-regular fa-star ${favorites.includes(place.place_id) ? 'yellow-star' : 'gray-star'
                        }`}
                    />
                  </div>

                  <div className="tile-buttons">
                    {place.phone_number && (
                      <button
                        className="contact-button"
                        onClick={() => (window.location.href = `tel:${place.phone_number}`)}
                      >
                        <i className="fa-solid fa-phone"></i>
                        <br />
                        <br />
                        <span className="phone-text">{place.phone_number}</span>
                      </button>
                    )}

                    {place.website && (
                      <button
                        onClick={() => window.open(place.website, "_blank")}
                        className="contact-button"
                      >
                        <i className="fa-solid fa-globe"></i>
                        <br />
                        <p>
                          {place.website
                            .replace(/^https?:\/\//, "") // remove http:// or https://
                            .replace(/^www\./, "") // remove www.
                            .split("/")[0]} {/* keep only domain */}
                        </p>
                      </button>

                    )}
                  </div>


                </div>
              ))}
            </div>
          )}
        </div>
      </div >
    </>
  );
}

export default ResultsPage;
